import React, { useMemo, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  ColorPicker,
  Alert,
  Space,
  Avatar,
} from "antd";
import { MaskedInput } from "antd-mask-input";

import useCreate from "hooks/useCreate";
import useFormErrors from "hooks/useFormErrors";
import { useQueryClient } from "@tanstack/react-query";
import { name_regexp } from "helpers/regexp";
import { useNavigate } from "react-router-dom";

const CreateWorkerInvitationModal = ({ user_type = "User::Worker" }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [color, setColor] = useState("#1677FF");
  const [is_open_create_modal, setOpenCreateModal] = useState(false);

  const bgColor = useMemo(() => color, [color]);
  const [form] = Form.useForm();

  const { pristine, has_error, onFieldsChange } = useFormErrors();

  const { is_saving, create } = useCreate({
    path: "/worker_invitations",
    form,
    success_message: "Zaproszenie zostało wysłane na maila.",
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ["worker_invitations"],
      });
      navigate("/worker_invitations");
    },
  });

  const handleOk = () => form.submit();
  const handleClose = () => setOpenCreateModal(false);

  const title =
    user_type === "User::Worker"
      ? "Zarejestruj pracownika"
      : "Zarejestruj administratora";

  return (
    <>
      <Button
        type="primary"
        htmlType="button"
        onClick={() => setOpenCreateModal(true)}
      >
        {title}
      </Button>
      <Modal
        style={{ top: 45 }}
        maskClosable={false}
        title={title}
        open={is_open_create_modal}
        onOk={handleOk}
        onCancel={handleClose}
        cancelText="Anuluj"
        okText="Dodaj"
        okButtonProps={{
          disabled: pristine || has_error,
          loading: is_saving,
        }}
      >
        <Alert
          className="m-t-10 m-b-20"
          title="Instrukcja"
          message={
            <>
              <p className="m-t-0">Wypełnij pola obowiazkowe.</p>
              <p>
                Na podany e-mail zostanie wysłane zaproszenie z linkiem do
                aplikacji mobilnej. Jednoczesnie ten e-mail służy do rejestracji
                i logowania.
              </p>
              <p>
                Wysłane zaproszenie będzie w zakładce{" "}
                <strong>Zaproszenia</strong>.
              </p>
              <p className="m-b-0">
                Musisz poczekać na rejestrację pracownika, aby pojawił się w
                zakładce <strong>Pracownicy</strong>.
              </p>
            </>
          }
        />
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={onFieldsChange}
          initialValues={{
            first_name: "",
            last_name: "",
            email: "",
          }}
          onFinish={async (values) => {
            try {
              await create({ user_type, color, ...values });
              form.resetFields();
              handleClose();
            } catch (error) {
              console.log(error);
            }
          }}
        >
          {(values) => (
            <>
              <Form.Item
                label="Imię"
                name="first_name"
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: "Imię jest wymagane!",
                  },
                  {
                    pattern: name_regexp,
                    message: "Pole zawiera niedowolone znaki!",
                  },
                ]}
              >
                <Input placeholder="Imię" maxLength={30} />
              </Form.Item>
              <Form.Item
                label="Nazwisko"
                name="last_name"
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: "Nazwisko jest wymagane!",
                  },
                  {
                    pattern: name_regexp,
                    message: "Pole zawiera niedowolone znaki!",
                  },
                ]}
              >
                <Input placeholder="Nazwisko" maxLength={30} />
              </Form.Item>
              <Form.Item
                label="E-mail"
                name="email"
                hasFeedback
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "E-mail jest niepoprawny!",
                  },
                ]}
              >
                <Input placeholder="E-mail" maxLength={60} />
              </Form.Item>
              <Form.Item
                label="Telefon"
                name="phone"
                required
                hasFeedback
                rules={[
                  () => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject(
                          new Error("Numer telefonu wymagany!")
                        );
                      }
                      if (value.includes("_")) {
                        return Promise.reject(
                          new Error("Numer telefonu niepoprawny!")
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <MaskedInput mask="000000000" />
              </Form.Item>
              <ColorPicker
                value={color}
                onChange={(value) => setColor(value.toHexString())}
                onChangeComplete={(value) =>
                  form.setFieldValue("color", value.toHexString())
                }
              >
                <Space>
                  <Avatar
                    shape={user_type === "User::Worker" ? "circle" : "square"}
                    style={{
                      cursor: "pointer",
                      backgroundColor: bgColor,
                    }}
                  >
                    {values.first_name?.charAt(0)?.toUpperCase()}
                    {values.last_name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Button type="primary" style={{ backgroundColor: bgColor }}>
                    Zmień kolor
                  </Button>
                </Space>
              </ColorPicker>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CreateWorkerInvitationModal;
